var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { InternationalPhoneInput } from './international-phone-input';
import { createCountryList, formatPhoneNumber, getCountryConstraints, getCountryPlaceholder, validatePhoneNumberForCountry, cleanPhoneInput, parseFullPhoneNumber, constructInternationalNumber, } from '../../../../shared/utils/phone-validation';
var IS_DIGIT = /\d/;
var ALL_COUNTRIES = createCountryList();
export var InternationalPhoneInputContainer = function (_a) {
    var _b;
    var value = _a.value, onChange = _a.onChange, _c = _a.defaultCountry, defaultCountry = _c === void 0 ? 'United States' : _c, disabled = _a.disabled, errorMessage = _a.errorMessage, inputProps = __rest(_a, ["value", "onChange", "defaultCountry", "disabled", "errorMessage"]);
    var defaultCountryCode = (_b = ALL_COUNTRIES.find(function (c) { return c.name === defaultCountry; })) === null || _b === void 0 ? void 0 : _b.code;
    var _d = useState(defaultCountryCode || 'US'), selectedCountry = _d[0], setSelectedCountry = _d[1];
    var _e = useState(''), phoneNumber = _e[0], setPhoneNumber = _e[1];
    var _f = useState(''), displayNumber = _f[0], setDisplayNumber = _f[1];
    var _g = useState({ isValid: false, isPossible: false }), validationState = _g[0], setValidationState = _g[1];
    // Ref to track if the phone number change is initiated internally
    var isInternalUpdate = useRef(false);
    // Effect to validate the phone number whenever it or the selected country changes
    useEffect(function () {
        if (phoneNumber && selectedCountry) {
            var fullNumber = constructInternationalNumber(phoneNumber, selectedCountry);
            var validation = validatePhoneNumberForCountry(fullNumber, selectedCountry);
            setValidationState(validation);
        }
        else {
            setValidationState({ isValid: false, isPossible: false });
        }
    }, [phoneNumber, selectedCountry]);
    // Effect to handle updates to the `value` prop from outside the component
    useEffect(function () {
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }
        if (value) {
            var _a = parseFullPhoneNumber(value, ALL_COUNTRIES, selectedCountry), nationalNumber = _a.nationalNumber, country = _a.country;
            if (country) {
                setSelectedCountry(country);
            }
            setPhoneNumber(nationalNumber);
            setDisplayNumber(formatPhoneNumber(nationalNumber, country || selectedCountry));
        }
        else {
            setPhoneNumber('');
            setDisplayNumber('');
        }
    }, [value]);
    var handleCountryChange = function (countryCode) {
        setSelectedCountry(countryCode);
        setPhoneNumber('');
        setDisplayNumber('');
        onChange('');
        setValidationState({ isValid: false, isPossible: false });
    };
    var handlePhoneChange = function (e) {
        var inputValue = e.target.value;
        if (inputValue === '') {
            setPhoneNumber('');
            setDisplayNumber('');
            onChange('');
            return;
        }
        var constraints = getCountryConstraints(selectedCountry);
        var newDigits = cleanPhoneInput(inputValue);
        if (newDigits.length > constraints.maxLength)
            return;
        if (newDigits !== phoneNumber) {
            setPhoneNumber(newDigits);
            var formattedNumber = formatPhoneNumber(newDigits, selectedCountry);
            setDisplayNumber(formattedNumber);
            isInternalUpdate.current = true;
            onChange(constructInternationalNumber(newDigits, selectedCountry));
        }
    };
    /**
     * Handles the keydown event to allow proper deletion of characters
     * when the input is formatted. For example, it allows deleting a digit
     * across a formatting character like a space or a parenthesis.
     */
    var handleKeyDown = function (e) {
        if (e.key === 'Backspace') {
            var input = e.target;
            var cursorPosition = input.selectionStart || 0;
            var currentValue = input.value;
            if (cursorPosition > 0 && !IS_DIGIT.test(currentValue[cursorPosition - 1])) {
                e.preventDefault();
                var digitIndex = -1;
                for (var i = cursorPosition - 1; i >= 0; i -= 1) {
                    if (IS_DIGIT.test(currentValue[i])) {
                        digitIndex = i;
                        break;
                    }
                }
                if (digitIndex >= 0) {
                    var currentDigits = phoneNumber;
                    var digitPosition = currentValue.substring(0, digitIndex + 1).replace(/\D/g, '').length - 1;
                    // Ensure the calculated digit position is valid before modifying the digits string
                    if (digitPosition >= 0 && digitPosition < currentDigits.length) {
                        var newDigits = currentDigits.substring(0, digitPosition) + currentDigits.substring(digitPosition + 1);
                        setPhoneNumber(newDigits);
                        var formattedNumber = newDigits ? formatPhoneNumber(newDigits, selectedCountry) : '';
                        setDisplayNumber(formattedNumber);
                        isInternalUpdate.current = true;
                        onChange(constructInternationalNumber(newDigits, selectedCountry));
                    }
                }
            }
        }
    };
    var handlePhoneBlur = function () {
        if (phoneNumber) {
            setDisplayNumber(formatPhoneNumber(phoneNumber, selectedCountry));
        }
    };
    var placeholder = getCountryPlaceholder(selectedCountry);
    var constraints = getCountryConstraints(selectedCountry);
    var shouldShowError = phoneNumber.length >= constraints.minLength && !validationState.isPossible;
    var displayErrorMessage = errorMessage || (shouldShowError ? 'Please enter a valid phone number' : undefined);
    return (_jsx(InternationalPhoneInput, __assign({}, inputProps, { value: displayNumber, onChange: handlePhoneChange, onBlur: handlePhoneBlur, onKeyDown: handleKeyDown, selectedCountry: selectedCountry, onCountryChange: handleCountryChange, countries: ALL_COUNTRIES, placeholder: placeholder, displayErrorMessage: displayErrorMessage, disabled: disabled })));
};
